import { useEffect, useState } from "react";
import { Download, BarChart3 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import * as TabsModule from "@/components/ui/tabs";
const Tabs: any = (TabsModule as any).Tabs;
const TabsList: any = (TabsModule as any).TabsList;
const TabsTrigger: any = (TabsModule as any).TabsTrigger;
const TabsContent: any = (TabsModule as any).TabsContent;
import { Skeleton } from "@/components/catalog/Skeleton";
import { downloadAuthed, formatMoney, reportsApi } from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";

const CHART_COLORS = ["#5e1a1a", "#8a2929", "#a04646", "#b46868", "#c98c8c", "#dcb1b1"];

type TabKey = "value" | "usage" | "condition" | "season" | "loans";

export default function Reports() {
  const [tab, setTab] = useState<TabKey>("value");

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="reports-page">
      <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Reporting</p>
      <h1 className="font-display text-3xl sm:text-4xl">Reports</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-prose">
        What's worn, what's idle, what's worth the most. Each tab exports to CSV for billing and insurance.
      </p>

      <Tabs value={tab} onValueChange={(v: any) => setTab(v as TabKey)} className="mt-8">
        <TabsList data-testid="reports-tablist" className="bg-bone/60 rounded-none">
          <TabsTrigger data-testid="tab-value" value="value">Inventory value</TabsTrigger>
          <TabsTrigger data-testid="tab-usage" value="usage">Usage</TabsTrigger>
          <TabsTrigger data-testid="tab-condition" value="condition">Condition</TabsTrigger>
          <TabsTrigger data-testid="tab-season" value="season">Season</TabsTrigger>
          <TabsTrigger data-testid="tab-loans" value="loans">Loans</TabsTrigger>
        </TabsList>

        <TabsContent value="value"><InventoryValueReport /></TabsContent>
        <TabsContent value="usage"><UsageReport /></TabsContent>
        <TabsContent value="condition"><ConditionReport /></TabsContent>
        <TabsContent value="season"><SeasonReport /></TabsContent>
        <TabsContent value="loans"><LoansReport /></TabsContent>
      </Tabs>
    </main>
  );
}

function ExportButton({ name }: { name: string }) {
  async function go() {
    const stamp = new Date().toISOString().slice(0, 10);
    await downloadAuthed(`/reports/export/${name}`, `${name}-${stamp}.csv`);
  }
  return (
    <Button variant="outline" onClick={go}
      data-testid={`export-${name}`}
      className="rounded-none border-foreground/20 hover:bg-bone h-9 text-xs">
      <Download className="w-3.5 h-3.5 mr-2" /> Export CSV
    </Button>
  );
}

function SectionHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between mb-4 mt-8 first:mt-0">
      <h2 className="font-display text-xl">{title}</h2>
      {action}
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-border bg-background p-5">
      <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</p>
      <p className="font-display text-2xl mt-2">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground italic py-6">{children}</p>;
}

/* ---------- Inventory value ---------- */
function InventoryValueReport() {
  const [data, setData] = useState<any | null>(null);
  useEffect(() => {
    reportsApi.inventoryValue().then(setData).catch(() => setData({}));
  }, []);
  if (!data) return <Skeleton className="h-72 mt-8" />;

  return (
    <div data-testid="report-inventory-value">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <StatCard label="Retail value" value={formatMoney(data.total_retail || 0)} />
        <StatCard label="Insured value" value={formatMoney(data.total_insured || 0)} />
      </div>

      <SectionHeader title="By category" action={<ExportButton name="inventory-value" />} />
      {(data.by_category || []).length === 0 ? (
        <EmptyState>No items catalogued yet.</EmptyState>
      ) : (
        <div className="border border-border bg-background p-4 h-80">
          <ResponsiveContainer>
            <BarChart data={data.by_category}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="category" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatMoney(Number(v))} />
              <Bar dataKey="insured_sum" fill="#5e1a1a" name="Insured" />
              <Bar dataKey="retail_sum" fill="#a04646" name="Retail" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <SectionHeader title="Top brands by insured value" />
      {(data.by_brand || []).length === 0 ? (
        <EmptyState>No brand data.</EmptyState>
      ) : (
        <ul className="border border-border divide-y divide-border bg-background">
          {data.by_brand.slice(0, 10).map((b: any) => (
            <li key={b.brand} className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-3 text-sm">
              <span className="font-medium truncate">{b.brand}</span>
              <span className="text-muted-foreground tabular-nums">{b.item_count} items</span>
              <span className="tabular-nums">{formatMoney(b.insured_sum)}</span>
            </li>
          ))}
        </ul>
      )}

      <SectionHeader title="Per-client breakdown" />
      {(data.by_client || []).length === 0 ? (
        <EmptyState>No client-tagged items yet.</EmptyState>
      ) : (
        <ul className="border border-border divide-y divide-border bg-background">
          {data.by_client.slice(0, 12).map((c: any) => (
            <li key={c.client_id} className="grid grid-cols-[1fr_auto_auto] gap-4 px-4 py-3 text-sm">
              <span className="font-medium truncate">{c.client_name}</span>
              <span className="text-muted-foreground tabular-nums">{c.item_count} items</span>
              <span className="tabular-nums">{formatMoney(c.insured_sum)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Usage ---------- */
function UsageReport() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState<any | null>(null);
  useEffect(() => {
    setData(null);
    reportsApi.usage(days).then(setData).catch(() => setData({}));
  }, [days]);

  return (
    <div data-testid="report-usage">
      <div className="flex items-center gap-2 mt-6">
        <span className="text-xs text-muted-foreground uppercase tracking-[0.18em]">Window</span>
        {[30, 90, 180, 365].map((d) => (
          <button key={d} type="button" onClick={() => setDays(d)}
            data-testid={`usage-window-${d}`}
            className={`px-3 py-1 text-xs border ${
              days === d ? "border-oxblood bg-oxblood/5 text-oxblood" : "border-border hover:bg-bone"
            }`}>{d}d</button>
        ))}
      </div>

      {!data ? <Skeleton className="h-60 mt-8" /> : (
        <>
          <SectionHeader title="Most-moved items" />
          {(data.most_moved || []).length === 0 ? (
            <EmptyState>No movements in this window.</EmptyState>
          ) : (
            <ul className="border border-border divide-y divide-border bg-background">
              {data.most_moved.slice(0, 12).map((r: any) => (
                <li key={r.item_id} className="grid grid-cols-[1fr_auto] px-4 py-3 text-sm">
                  <span className="truncate">{r.name} <span className="text-muted-foreground">{r.brand}</span></span>
                  <span className="tabular-nums">{r.moves} moves</span>
                </li>
              ))}
            </ul>
          )}

          <SectionHeader title="Most-loaned items" />
          {(data.most_loaned || []).length === 0 ? (
            <EmptyState>No loans in this window.</EmptyState>
          ) : (
            <ul className="border border-border divide-y divide-border bg-background">
              {data.most_loaned.slice(0, 12).map((r: any) => (
                <li key={r.item_id} className="grid grid-cols-[1fr_auto] px-4 py-3 text-sm">
                  <span className="truncate">{r.name} <span className="text-muted-foreground">{r.brand}</span></span>
                  <span className="tabular-nums">{r.loans} loans</span>
                </li>
              ))}
            </ul>
          )}

          <SectionHeader title="Dormant pieces" />
          {(data.dormant || []).length === 0 ? (
            <EmptyState>Everything has been touched in the last 180 days.</EmptyState>
          ) : (
            <ul className="border border-border divide-y divide-border bg-background">
              {data.dormant.slice(0, 12).map((r: any) => (
                <li key={r.id} className="px-4 py-3 text-sm truncate">
                  {r.name} <span className="text-muted-foreground">— {r.brand || r.category || "no brand"}</span>
                </li>
              ))}
            </ul>
          )}

          <SectionHeader title="Never moved" />
          {(data.never_moved || []).length === 0 ? (
            <EmptyState>Every catalogued piece has at least one movement.</EmptyState>
          ) : (
            <ul className="border border-border divide-y divide-border bg-background">
              {data.never_moved.slice(0, 12).map((r: any) => (
                <li key={r.id} className="px-4 py-3 text-sm truncate">
                  {r.name} <span className="text-muted-foreground">— {r.brand || r.category || "no brand"}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

/* ---------- Condition ---------- */
function ConditionReport() {
  const [data, setData] = useState<any | null>(null);
  useEffect(() => {
    reportsApi.conditionHealth().then(setData).catch(() => setData({}));
  }, []);
  if (!data) return <Skeleton className="h-72 mt-8" />;

  return (
    <div data-testid="report-condition">
      <SectionHeader title="By condition" action={<ExportButton name="condition-health" />} />
      {(data.by_condition || []).length === 0 ? (
        <EmptyState>No items yet.</EmptyState>
      ) : (
        <div className="border border-border bg-background p-4 h-72">
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data.by_condition} dataKey="count" nameKey="condition" outerRadius={100} label>
                {(data.by_condition as any[]).map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      <SectionHeader title="Needs repair" />
      {(data.needs_repair || []).length === 0 ? (
        <EmptyState>Nothing flagged for repair.</EmptyState>
      ) : (
        <ul className="border border-border divide-y divide-border bg-background">
          {data.needs_repair.map((r: any) => (
            <li key={r.id} className="px-4 py-3 text-sm">
              <p className="font-medium truncate">{r.name} <span className="text-muted-foreground">{r.brand}</span></p>
              {r.notes && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{r.notes}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Season ---------- */
function SeasonReport() {
  const [data, setData] = useState<any | null>(null);
  useEffect(() => {
    reportsApi.seasonCoverage().then(setData).catch(() => setData({}));
  }, []);
  if (!data) return <Skeleton className="h-72 mt-8" />;

  return (
    <div data-testid="report-season">
      <SectionHeader title="By season & category" action={<ExportButton name="season-coverage" />} />
      {(data.seasons || []).length === 0 ? (
        <EmptyState>No items tagged by season yet.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.seasons.map((s: any) => (
            <div key={s.season} className="border border-border bg-background p-5">
              <p className="text-[10px] uppercase tracking-[0.22em] text-oxblood font-medium">{s.season}</p>
              <ul className="mt-3 space-y-1.5">
                {s.categories.map((c: any) => (
                  <li key={c.category} className="flex justify-between text-sm">
                    <span>{c.category}</span>
                    <span className="tabular-nums text-muted-foreground">{c.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Loans throughput ---------- */
function LoansReport() {
  const [data, setData] = useState<any | null>(null);
  useEffect(() => {
    reportsApi.loansThroughput().then(setData).catch(() => setData({}));
  }, []);
  if (!data) return <Skeleton className="h-72 mt-8" />;

  return (
    <div data-testid="report-loans">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <StatCard label="Total returned" value={String(data.total_returned || 0)} />
        <StatCard label="Avg loan length" value={`${data.avg_loan_days || 0} days`} />
        <StatCard label="Downgrade rate"
          value={`${Math.round((data.downgrade_rate || 0) * 100)}%`}
          hint="Returned needing cleaning, repair, or worse." />
      </div>

      <SectionHeader title="Loans per month" action={<ExportButton name="loans-throughput" />} />
      {(data.monthly_counts || []).length === 0 ? (
        <EmptyState>No loans on record.</EmptyState>
      ) : (
        <div className="border border-border bg-background p-4 h-72">
          <ResponsiveContainer>
            <LineChart data={data.monthly_counts}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#5e1a1a" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
